import {Box} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useEffect} from 'react';
import {TITLE_SUFFIX} from '../../../config/constants';
import {
  COLUMN_CRUMB,
  COLUMN_SPECIAL_CRUMB,
} from '../../../config/constants/crumb';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {COLUMNS, COLUMN_SPECIAL_INDEX} from '../../../config/constants/routes';
import type {PostSchema} from '../../../core/entities/columns/schema';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {BreadcrumbsComponent} from '../../../shared/components/BreadcrumbsComponent';
import {createArticleDetailStructuredData} from '../../../shared/services/createArticleDetailStructuredDate';
import {createBreadCrumbsStructuredData} from '../../../shared/services/createBreadCrumbsStructuredData';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {CategoryList} from './Container/CategoryList';
import {SpecialColumnPostTitle} from './Container/Presenter/SpecialColumnPostTitle';
import {SpecialColumnLinkList} from './Container/SpecialColumnLinkList';
import {TableOfContents} from './Container/TableOfContents';
import {SpecialPostContent} from './SpecialPostContent';

type Props = {
  slug: string;
  parsedContent: string;
  isWpContent: boolean;
  post: PostSchema;
  metaDescription?: string;
};

export type ColumnClip = {
  isClipped: boolean;
  clipCount: number;
  handleClip: () => Promise<void>;
};

export const SpecialPage: React.FC<Props> = ({
  slug,
  post,
  parsedContent,
  isWpContent,
  metaDescription,
}: Props) => {
  const router = useRouter();
  /*
   * ホスト名がlocalhostになっているため、OGPやSEOのURLを指定する要素に使えない.
   * 一旦ベタ書きで対応し、後に修正する.
   * todo URLのベタ書きを環境変数等から取得するように修正する.
   */
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;

  const {isPC} = useMediaQuery();
  const googleAdBannerNames = [GoogleAdBannerName.D_FEED_LEFT_SMALL];
  const {setShouldRefetch} = useGoogleAdBanner(googleAdBannerNames);

  const {title} = post;

  const crumbs = [
    {label: COLUMN_CRUMB, href: COLUMNS},
    {label: COLUMN_SPECIAL_CRUMB, href: COLUMN_SPECIAL_INDEX},
  ];

  useEffect(() => {
    setShouldRefetch(true);
  }, []);

  return (
    <>
      <Head>
        <title key="title">{`${title}${TITLE_SUFFIX}`}</title>
        <meta
          key="og:title"
          property="og:title"
          content={`${title}${TITLE_SUFFIX}`}
        />
        {metaDescription && (
          <>
            <meta
              key="description"
              name="description"
              content={metaDescription}
            />
            <meta
              key="og:description"
              property="og:description"
              content={metaDescription}
            />
            <meta
              key="twitter:description"
              name="twitter:description"
              content={metaDescription}
            />
          </>
        )}
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta key="og:image" property="og:image" content={post.ogpImageURL} />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={`${title}${TITLE_SUFFIX}`}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={post.ogpImageURL}
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
        <script type="application/ld+json">
          {JSON.stringify(createBreadCrumbsStructuredData(crumbs))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(
            createArticleDetailStructuredData(post, metaDescription),
          )}
        </script>
        <meta name="robots" content="max-image-preview:large" />
      </Head>

      <Box className="mb-8 ml-12">
        <BreadcrumbsComponent crumbs={crumbs} />
      </Box>

      <div className="w-full">
        <SpecialColumnPostTitle
          slug={slug}
          title={title}
          datetime={post.updateDateTime}
          tags={post.tags}
        />
        <div className="w-full flex justify-between">
          {isPC && (
            <div className="sticky top-[150px] self-start w-[200px]">
              <TableOfContents
                columnId={post.id}
                title={title}
                currentUrl={currentUrl}
              />
              <SpecialColumnLinkList />
              <CategoryList currentPagePath={currentUrl} />
              <GoogleAdBanner
                googleAdBannerName={GoogleAdBannerName.D_FEED_LEFT_SMALL}
              />
            </div>
          )}
          <SpecialPostContent
            slug={slug}
            columnId={post.id}
            parsedContent={parsedContent}
            isWpContent={isWpContent}
            title={title}
            currentUrl={currentUrl}
          />
        </div>
      </div>
    </>
  );
};
